import { Component, useId, useRef, useState, type ReactNode } from 'react'
import { Ellipsis, Info } from 'lucide-react'

import type { HomeWidgetDefinition } from '@/lib/home-widgets'
import { Button } from '@/ui/button'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/ui/card'
import {
  Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogTitle,
} from '@/ui/dialog'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/ui/dropdown-menu'
import { Skeleton } from '@/ui/skeleton'

export function HomeWidgetLoading() {
  return (
    <div data-slot="home-widget-loading" role="status" className="space-y-3">
      <span className="sr-only">Loading widget</span>
      <div aria-hidden="true" className="space-y-3">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  )
}

export function HomeWidgetEmpty({ children = 'Nothing to show yet.' }: { children?: ReactNode }) {
  return <p role="status" data-slot="home-widget-empty" className="text-muted-foreground">{children}</p>
}

export function HomeWidgetError({ onRetry }: { onRetry?: () => void }) {
  return (
    <div data-slot="home-widget-error" className="space-y-3">
      <p role="alert">This widget couldn’t load.</p>
      {onRetry && <Button variant="outline" size="sm" onClick={onRetry}>Try again</Button>}
    </div>
  )
}

export function HomeWidgetPlaceholder() {
  return (
    <div data-slot="home-widget-placeholder" className="text-muted-foreground flex h-full min-h-12 items-center justify-center gap-2 text-center">
      <Info aria-hidden className="size-4 shrink-0" />
      <p>Coming soon</p>
    </div>
  )
}

class WidgetBoundary extends Component<{ children: ReactNode; fallback: ReactNode }, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}

type HomeWidgetControls = { handle?: ReactNode; onRemove?: () => void }

function WidgetFrame({ widget, entityId, handle, onRemove }: { widget: HomeWidgetDefinition; entityId: string } & HomeWidgetControls) {
  const titleId = useId()
  const menuRef = useRef<HTMLButtonElement>(null)
  const [aboutOpen, setAboutOpen] = useState(false)
  const Icon = widget.icon
  const Body = widget.component
  const RelatedLink = widget.viewAll

  return (
    <Card data-slot="home-widget" data-widget-id={widget.id} role="group" aria-labelledby={titleId} className="h-full min-w-0">
      <CardHeader>
        <CardTitle as="h2" id={titleId} className="flex min-w-0 items-center gap-2">
          {Icon && <Icon aria-hidden className="text-muted-foreground size-4 shrink-0" />}
          <span className="min-w-0 break-words">{widget.name}</span>
        </CardTitle>
        <CardAction className="flex items-center gap-1">
          {handle}
          <DropdownMenu>
            <DropdownMenuTrigger ref={menuRef} render={<Button variant="ghost" size="icon-sm" className="focus-visible:ring-foreground" aria-label={`${widget.name} menu`} />}>
              <Ellipsis aria-hidden />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => setAboutOpen(true)}>About this widget</DropdownMenuItem>
              {onRemove && <DropdownMenuItem variant="destructive" onClick={onRemove}>Remove widget</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </CardAction>
        {RelatedLink && (
          <RelatedLink entityId={entityId} className="text-primary dark:text-foreground w-fit underline-offset-4 hover:underline" />
        )}
      </CardHeader>
      <CardContent className="min-w-0 flex-1 break-words">
        <Body entityId={entityId} />
      </CardContent>
      <Dialog open={aboutOpen} onOpenChange={setAboutOpen}>
        <DialogContent finalFocus={menuRef} className="max-w-[min(28rem,calc(100vw-2rem))]">
          <DialogTitle>About {widget.name}</DialogTitle>
          <DialogDescription>{widget.description}</DialogDescription>
          <DialogFooter>
            <DialogClose render={<Button variant="outline" size="sm" />}>Close</DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

function FailedWidgetBody() {
  return <HomeWidgetError />
}

export function HomeWidget({ widget, entityId, ...controls }: { widget: HomeWidgetDefinition; entityId: string } & HomeWidgetControls) {
  return (
    <WidgetBoundary key={`${entityId}:${widget.id}`} fallback={
      <WidgetFrame widget={{ ...widget, icon: undefined, viewAll: undefined, component: FailedWidgetBody }} entityId={entityId} {...controls} />
    }>
      <WidgetFrame widget={widget} entityId={entityId} {...controls} />
    </WidgetBoundary>
  )
}
